<template>
  <div class="chart resale_status_purchase" style="height:100%" :ref="[refName]"></div>
</template>

<script>
import * as am4core from "@amcharts/amcharts4/core";
import * as am4charts from "@amcharts/amcharts4/charts";
import am4themes_animated from "@amcharts/amcharts4/themes/animated";
import { numberWithCommas } from 'ops2clientlibrary/src/library/regexp'
import {CHART_COLORS, OPACITY_VALUES} from '../../code/CodeChart';

export default {
  name: 'stack-column-chart',
  props: {
    refName: {
      type: String,
      required: true
    },
    chartData: {
      type: Array,
      default: () => []
    },
    filter: {
      type: Array,
      default: () => []
    },
    numberFormatter: {
      type: Object,
      default : () => {
        return {
          numberFormat:"#a",
          bigNumberPrefixes: [
            { "number": 1e+3, "suffix": "천" },
            { "number": 1e+4, "suffix": "만" },
            { "number": 1e+8, "suffix": "억" }
          ]
        }
      }
    },
    numberPrefix: {
      type: String, 
      default: "₩"
    },
    stacked: {
      type: Boolean,
      default: false
    },
    useMinGridDistance: {
      type: Boolean,
      default: true
    },
    mouseCursorStyle: {
      type: Array,
      default: am4core.MouseCursorStyle.pointer
    },
    useCursorToolTip: {
      type: Boolean, 
      default: false
    },
    showColumnTotal: {
      type: Boolean,
      default: true
    }
  }, watch: {
    chartData: function () {
      this.startData()
    }
  },
  data () {
    return {
      chart: null,
      colorFilter: {},
      seriesName: {}
    }
  },
  computed: {
    chartFilter () {
      return this.filter.map(item => typeof(item) === 'string' ? item : item.column)
    }
  },
  methods: {
    createCategoryAxies () {
      let categoryAxis = this.chart.xAxes.push(new am4charts.CategoryAxis());
      categoryAxis.dataFields.category = "category";
      categoryAxis.fontSize = 12;
      categoryAxis.renderer.grid.template.disabled = true;
      categoryAxis.tooltip.disabled = true;
      categoryAxis.snapTooltip = true;
      if(this.useMinGridDistance){
        categoryAxis.renderer.minGridDistance = 30;
      }

      if(this.chartData.length < 3){
        categoryAxis.renderer.cellStartLocation = 0.3;
        categoryAxis.renderer.cellEndLocation = 0.7;
      }else{
        categoryAxis.renderer.cellStartLocation = 0.1;
        categoryAxis.renderer.cellEndLocation = 0.9;
      }

      return categoryAxis
    },
    getMinChartValue () {
      const minVal = Math.min(...this.chartData.map(item => Math.min(...Object.values(item).filter(arr => typeof(arr) === 'number'))))
      return minVal < 0 ? minVal : 0;
    },
    createValueAxis () {
      let valueAxis = this.chart.yAxes.push(new am4charts.ValueAxis())
      valueAxis.tooltip.disabled = true;
      valueAxis.fontSize = 12;
      valueAxis.strictMinMax = true
      valueAxis.min = this.getMinChartValue();
      const maxVal = Math.max(...this.chartData.map(item => Object.values(item).filter(arr => typeof(arr) === 'number')))
      const sumVal = Math.max(...this.chartData.map(item => Object.values(item).filter(arr => typeof(arr) === 'number').reduce((a, b) => a + b, 0)))
      if(this.stacked){
        if(sumVal < 5){
          valueAxis.max = 5;
        }
      }else{
        if(maxVal < 5){
          valueAxis.max = 5;
        }
      }

      return valueAxis
    },
    createTopContainer () {
      let topContainer = this.chart.chartContainer.createChild(am4core.Container);
      topContainer.layout = "absolute";
      topContainer.toBack();
      topContainer.paddingBottom = 15;
      topContainer.width = am4core.percent(100);
    },
    createCursor() {
      let cursor = new am4charts.XYCursor();
      cursor.lineX.disabled = !this.useCursorToolTip;
      cursor.lineY.disabled = true;
      return cursor
    },
    createSeries (field, name, color) {
      //##Set up series
      let series = this.chart.series.push(new am4charts.ColumnSeries());
      //##Make it stacked
      series.stacked = this.stacked;
      series.name = name;
      series.dataFields.valueY = field.replace(/\./g,"");
      series.dataFields.categoryX = "category";

      // series.sequencedInterpolation = true;
      series.showOnInit = false;
      series.stroke = null;
      series.fill  = color;
      series.events.on("over", this.getOverCallback(this.chart, self));
      series.events.on("out", this.getOutCallback(this.chart, self));
      series.tooltip.pointerOrientation = "horizontal"
      series.columns.template.tooltipPosition = 'pointer';
      series.tooltip.getFillFromObject = false;

      series.tooltip.background.fill = am4core.color("rgba(255, 255, 255, 1)");
      series.tooltip.background.fillOpacity = OPACITY_VALUES.DEFAULT;
      series.tooltip.background.cornerRadius = 0;
      series.tooltip.strokeOpacity = 1;

      series.columns.template.cursorOverStyle = this.mouseCursorStyle;
      series.cursorTooltipEnabled = this.useCursorToolTip;
      series.columns.template.height = am4core.percent(100);
      series.columns.template.width = am4core.percent(100);
      series.columns.template.autoMargins= false;
      series.columns.template.marginLeft= 0;
      series.columns.template.marginRight= 0;
      series.sequencedInterpolation = true;

      series.columns.template.tooltipHTML = '<span>test</span>';
      series.columns.template.events.on('hit', function (ev) {
        this.$emit('hit', ev)
      }, this)
      series.columns.template.adapter.add('tooltipHTML', (html, target) => {
        
        if (target.dataItem && (target.dataItem.value < 0)) {
          return html
        }
        
        let columnData = target.dataItem.dataContext;
        const total = columnData.total || this.chartFilter.map(item => {
          return columnData[item]
        }).reduce((acc, cur) => acc + cur, 0)
        return `
          <div class="amchart-custom-tooltip -p-1 -pb-2">
            <h2 class="-font-size-12 -color-darkgray-1 -font-weight-regular -pb-2">${columnData.category}</h2>
              ${Object.keys(columnData).sort().filter(key => {
                  return this.chartFilter.includes(key)
                })
                .map(key => this.getTooltipHtmlByCategoryField(columnData, key, this.colorFilter, this.seriesName))
                .join('')
              }` + 
              (this.showColumnTotal? `
                <div class="total -color-darkgray-1 chart-lr-grid v-center -width-100 -pt-2 -mt-2 -border-gray-2">
                  <em>Total :</em>
                  <span>${this.numberPrefix}${numberWithCommas(total || '-')}</span>
                </div>`: '') + 
            `</div>`;
      });
      return series;
    },
    columnEventBind (series) {
        series.events.on("over", this.getOverCallback(this.chart, this));
        series.events.on("out", this.getOutCallback(this.chart, this));
    },
    getOverCallback (chart) {
      return ($vm) => {
        let evTarget = $vm.target;
        const hoveredColumn = this.getHoveredColumn(evTarget.columns);
        this.fillOpacityForColumnsWhenMouseOver(chart, hoveredColumn);
      }
    },
    getOutCallback (chart) {
      return function () {
        chart.series.each(function (columnSeries) {
          columnSeries.columns.each(function(column) {
            const col = column;
            if (col.fillOpacity !== OPACITY_VALUES.NO_CHANGE) {
              col.fillOpacity = OPACITY_VALUES.DEFAULT;
            }
          });
        });
      }
    },
    getHoveredColumn (columns) {
      let hoveredColumn = null;
      columns.each(function(column) {
        const col = column;
        if (col.isHover) {
          hoveredColumn = column;
          return;
        }
      });
      return hoveredColumn;
    },
    fillOpacityForColumnsWhenMouseOver (chart, hoveredColumn) {
      chart.series.each(function (columnSeries) {
      columnSeries.columns.each(function(column) {
          let col = column;
          if (col.fillOpacity !== OPACITY_VALUES.NO_CHANGE) {
            if (column.dataItem.categoryX !== hoveredColumn.dataItem.categoryX) {
              col.fillOpacity = OPACITY_VALUES.FADEOUT;
            } else {
              col.fillOpacity = OPACITY_VALUES.DEFAULT;
            }
          }
        });
      });
    },
    getTooltipHtmlByCategoryField(columnData, key, colorFilter, seriesName) {
      return `<div class="chart-lr-grid v-center -pt-1 -font-size-12">
          <p class="-color-darkgray-1 flex-ui center">
            <span class="legend-mark -mr-2" style="background-color:${colorFilter[key]}"></span>
            <span class="legend-text">${seriesName[key]}</span>
          </p>
          <p class="-color-darkgray-1 ">${this.numberPrefix}${numberWithCommas(columnData[key])}</p>
        </div>`;
    },

    createBullet () {},
    createLegned () {
      this.chart.legend = new am4charts.Legend();
      this.chart.legend.position = "bottom";
      this.chart.legend.marginLeft = 42;
      this.chart.legend.fontSize = 12;
      /*label marker styling*/
      this.chart.legend.useDefaultMarker = true;
      var marker = this.chart.legend.markers.template.children.getIndex(0);
      marker.cornerRadius(0, 0, 0, 0);

      var markerTemplate = this.chart.legend.markers.template;
      markerTemplate.width = 16;
      markerTemplate.height = 8;

      this.chart.legend.itemContainers.template.paddingTop = 0;
      this.chart.legend.itemContainers.template.paddingBottom = 0;
      if (this.numberFormatter) {
        this.chart.numberFormatter.numberFormat = this.numberFormatter.numberFormat;
        this.chart.numberFormatter.bigNumberPrefixes = this.numberFormatter.bigNumberPrefixes
      }
    },
    startData () {
      if (this.chart) {
        this.chart.dispose();
      }
      // 스킨설정
      am4core.useTheme(am4themes_animated);
      this.chart = am4core.create(this.$refs[this.refName], am4charts.XYChart)

      // 커스터마이징 시 자동으로 클래스가 추가됨?
      am4core.options.autoSetClassName = true;

      // 차트 데이터 지정
      this.chart.data = this.chartData

      // X축은 카테고리기반 축 생성(Category axis)
      this.createCategoryAxies()
      this.createValueAxis()
      this.createTopContainer()
      this.chart.cursor = this.createCursor();

      this.colorFilter = {};
      this.seriesName = {};
      for (var i = 0; i < this.chartFilter.length; i++) {
        this.colorFilter[this.chartFilter[i]] = CHART_COLORS[i];
        if (typeof(this.filter[i]) === 'string') {
          this.seriesName[this.chartFilter[i]] = this.filter[i]
          this.createSeries(this.filter[i], this.filter[i], CHART_COLORS[i]);
        } else {
          this.seriesName[this.chartFilter[i]] = this.filter[i].label
          this.createSeries(this.filter[i].column, this.filter[i].label, CHART_COLORS[i]);
        }
      }
      this.createLegned()
    }
  },
  mounted () {
    this.startData()
  },
  beforeDestroy() {
    if (this.chart) {
      this.chart.dispose();
    }
  }
}
</script>
<style scoped>
  .resale_status_purchase.chart {
    height: 100%;
  }
</style>



// WEBPACK FOOTER //
// src/pages/components/StackColumnChart.vue